import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  FileSpreadsheet, 
  TrendingUp, 
  Landmark, 
  Calculator,
  Users,
  FileText,
  ArrowRight,
  CheckCircle,
  Globe
} from 'lucide-react';

const Landing = () => {
  const { t, i18n } = useTranslation();

  const features = [
    {
      icon: FileSpreadsheet,
      title: t('landing.features.balance'),
      description: t('landing.features.balanceDesc'),
      color: 'from-violet-500 to-indigo-500'
    },
    {
      icon: TrendingUp,
      title: t('landing.features.bp'),
      description: t('landing.features.bpDesc'),
      color: 'from-cyan-500 to-teal-500'
    },
    {
      icon: Landmark,
      title: t('landing.features.pf'),
      description: t('landing.features.pfDesc'),
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Calculator,
      title: t('landing.features.val'),
      description: t('landing.features.valDesc'),
      color: 'from-amber-500 to-orange-500'
    }
  ];

  const benefits = [
    "Calcolo automatico degli indici finanziari",
    "Proiezioni finanziarie multi-anno",
    "Analisi VAN, TIR e Payback",
    "Valutazione DCF e multipli di mercato",
    "Collaborazione multi-utente",
    "Export PDF, Excel e Word"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-bold text-2xl gradient-text" data-testid="landing-logo">
              AI Finance Management
            </Link>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => i18n.changeLanguage(i18n.language === 'it' ? 'en' : 'it')}
                data-testid="lang-toggle"
              >
                <Globe size={18} className="mr-2" />
                {i18n.language === 'it' ? 'EN' : 'IT'}
              </Button>
              <Link to="/login">
                <Button variant="ghost" data-testid="login-btn">{t('auth.login')}</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:opacity-90 shadow-lg" data-testid="register-btn">
                  {t('auth.register')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-100 via-transparent to-transparent opacity-50" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fadeIn">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
                Il futuro dell'Analisi Finanziaria
                <span className="block gradient-text mt-2">Semplice, immediato, efficace.</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
                {t('landing.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:opacity-90 shadow-lg text-lg px-8 py-6 rounded-full btn-scale"
                    data-testid="hero-cta"
                  >
                    {t('landing.hero.cta')}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="text-lg px-8 py-6 rounded-full"
                    data-testid="hero-login"
                  >
                    {t('auth.login')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-gradient-to-r from-violet-400 to-indigo-400 rounded-3xl blur-3xl opacity-20" />
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" 
                alt="Financial Dashboard"
                className="rounded-2xl shadow-2xl relative z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tutti gli strumenti necessari per l'analisi finanziaria professionale
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="group card-hover border-0 shadow-lg bg-white"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6 space-y-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                    <feature.icon size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80" 
                alt="Team collaboration"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                {t('landing.collaboration.title')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('landing.collaboration.desc')}
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle size={16} className="text-green-600" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Export Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {t('landing.export.title')}
          </h2>
          <p className="text-lg text-slate-600 mb-12 max-w-2xl mx-auto">
            {t('landing.export.desc')}
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <Card className="p-8 shadow-lg border-0 card-hover">
              <CardContent className="flex flex-col items-center gap-4 p-0">
                <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center">
                  <FileText size={32} className="text-red-600" />
                </div>
                <span className="font-semibold text-lg">PDF</span>
              </CardContent>
            </Card>
            <Card className="p-8 shadow-lg border-0 card-hover">
              <CardContent className="flex flex-col items-center gap-4 p-0">
                <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
                  <FileSpreadsheet size={32} className="text-green-600" />
                </div>
                <span className="font-semibold text-lg">Excel</span>
              </CardContent>
            </Card>
            <Card className="p-8 shadow-lg border-0 card-hover">
              <CardContent className="flex flex-col items-center gap-4 p-0">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <FileText size={32} className="text-blue-600" />
                </div>
                <span className="font-semibold text-lg">Word</span>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-violet-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Inizia oggi la tua analisi finanziaria
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Registrati gratuitamente e scopri tutte le funzionalità della piattaforma
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-violet-600 hover:bg-white/90 shadow-lg text-lg px-8 py-6 rounded-full btn-scale"
              data-testid="cta-register"
            >
              {t('landing.hero.cta')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center">
            <span className="text-slate-400">© 2025 AI Finance Management. All Rights Reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
